import React from 'react';
import { useWishlist } from './WishlistContext';

/**
 * Purchase-oriented product card used inside a vendor storefront.
 *
 * The product itself opens the product detail page. Cart controls remain a
 * separate action so discovery and purchase are both available without
 * making the whole card behave like a button.
 */
const VendorProductCard = ({
  product,
  vendor,
  getImageUrl,
  formatPrice,
  quantity = 0,
  onView,
  onShare,
  onAdd,
  onIncrement,
  onDecrement,
}) => {
  const wishlist = useWishlist();
  if (!product) return null;

  const price = Number(product.price || 0);
  const originalPrice = Number(product.original_price || 0);
  const hasRealDeal = Boolean(product.is_flash_deal && originalPrice > price);
  const discount = hasRealDeal
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : 0;
  const inWishlist = wishlist.isInWishlist(product.id);
  const isAvailable = product.available !== false;
  const isOpen = vendor?.is_open !== false;
  const hasQuantity = quantity > 0;

  const priceText = formatPrice
    ? formatPrice(price)
    : `₦${price.toLocaleString('en-NG')}`;
  const originalPriceText = formatPrice
    ? formatPrice(originalPrice)
    : `₦${originalPrice.toLocaleString('en-NG')}`;

  return (
    <article
      data-testid={`vendor-product-card-${product.id}`}
      className={`group bg-white border rounded-3xl overflow-hidden transition-all active:scale-[0.99] ${
        hasQuantity
          ? 'border-cyan-500 shadow-md'
          : 'border-gray-200 hover:border-cyan-500 hover:shadow-md'
      }`}
    >
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        {discount > 0 && (
          <span className="absolute top-2 left-2 z-10 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            -{discount}%
          </span>
        )}

        <div className="absolute top-2 right-2 z-10 flex gap-1.5">
          {onShare && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onShare(product, e);
              }}
              data-testid={`vendor-product-share-${product.id}`}
              className="w-10 h-10 sm:w-9 sm:h-9 rounded-full bg-white/95 backdrop-blur text-gray-500 hover:text-cyan-700 active:scale-95 flex items-center justify-center transition-colors shadow-sm touch-manipulation"
              aria-label={`Share ${product.name}`}
            >
              <i className="fa-solid fa-share-nodes text-xs" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              wishlist.toggleWishlist({ ...product, vendor_name: vendor?.name });
            }}
            data-testid={`vendor-product-wishlist-${product.id}`}
            className={`w-10 h-10 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors shadow-sm active:scale-95 touch-manipulation ${
              inWishlist
                ? 'bg-red-500 text-white'
                : 'bg-white/95 backdrop-blur text-gray-500 hover:text-red-500'
            }`}
            aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <i className={`${inWishlist ? 'fa-solid' : 'fa-regular'} fa-heart text-xs`} />
          </button>
        </div>

        <button
          type="button"
          onClick={() => isAvailable && onView?.(product)}
          className={`w-full h-full block ${isAvailable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
          aria-label={`View ${product.name}`}
        >
          {!isAvailable && (
            <span className="absolute inset-0 z-10 bg-black/45 flex items-center justify-center text-white text-xs font-bold">
              Currently unavailable
            </span>
          )}

          <img
            src={getImageUrl ? getImageUrl(product.image_url) : product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = 'https://via.placeholder.com/300?text=Product';
            }}
          />
        </button>
      </div>

      <div className="p-3 sm:p-3.5">
        <button
          type="button"
          onClick={() => isAvailable && onView?.(product)}
          className={`w-full text-left ${isAvailable ? '' : 'cursor-not-allowed'}`}
        >
          <p className="text-xs sm:text-sm font-bold text-gray-900 line-clamp-2 leading-snug min-h-[2.35rem]">
            {product.name}
          </p>
        </button>

        {product.description && (
          <p className="text-[11px] text-gray-400 line-clamp-1 mt-1">
            {product.description}
          </p>
        )}

        <div className="mt-2 flex items-baseline gap-1.5 flex-wrap">
          <span className="text-base sm:text-lg font-extrabold text-red-600">{priceText}</span>
          {hasRealDeal && (
            <span className="text-xs text-gray-400 line-through">{originalPriceText}</span>
          )}
        </div>

        <div className="mt-2.5">
          {hasQuantity ? (
            <div className="flex items-center justify-between gap-2 bg-cyan-50 border border-cyan-200 rounded-lg px-1.5 py-1.5">
              <button
                type="button"
                onClick={() => onDecrement?.(product)}
                data-testid={`vendor-decrement-${product.id}`}
                className="w-10 h-10 sm:w-9 sm:h-9 rounded-lg bg-white border border-cyan-300 hover:bg-cyan-100 active:scale-95 text-cyan-700 font-bold text-base flex items-center justify-center touch-manipulation"
                aria-label={`Decrease ${product.name} quantity`}
              >
                -
              </button>
              <span className="text-sm font-bold text-cyan-800 min-w-[2rem] text-center">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => onIncrement?.(product)}
                data-testid={`vendor-increment-${product.id}`}
                className="w-10 h-10 sm:w-9 sm:h-9 rounded-lg bg-white border border-cyan-300 hover:bg-cyan-100 active:scale-95 text-cyan-700 font-bold text-base flex items-center justify-center touch-manipulation"
                aria-label={`Increase ${product.name} quantity`}
              >
                +
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onAdd?.(product)}
              disabled={!isOpen || !isAvailable}
              data-testid={`vendor-add-${product.id}`}
              className={`w-full min-h-10 sm:min-h-11 font-bold py-2 rounded-lg text-xs sm:text-sm transition-colors flex items-center justify-center gap-1.5 touch-manipulation ${
                isOpen
                  ? 'bg-cyan-500 hover:bg-cyan-700 text-white'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <i className={`fa-solid ${isOpen ? 'fa-plus' : 'fa-store-slash'} text-[10px]`} />
              {!isAvailable ? 'Unavailable' : isOpen ? 'Add to Cart' : 'Vendor Closed'}
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default VendorProductCard;




